import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { filter } from 'rxjs';
import { PriceCurrencyPipe } from '@/common/pipes/price-currency.pipe';
import {
	SettlementBonusRowActions,
	SettlementBonusStatus,
} from '@/settlement/settlement-bonus-list-v2/_models/settlement-bonus-list-v2.model';
import { DriversSalaryV2StateService } from '../_services/drivers-salary-v2.state.service';
import { RejectBonusDialogComponent } from './reject-bonus-dialog.component';

@Component({
	selector: 'app-salary-settlement-bonus-row',
	standalone: true,
	imports: [DatePipe, DecimalPipe, RouterLink, MatButtonModule, MatIconModule, MatTooltipModule, PriceCurrencyPipe],
	templateUrl: './salary-settlement-bonus-row.component.html',
	styleUrl: './salary-settlement-bonus-row.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SalarySettlementBonusRowComponent {
	readonly bonusId = input.required<string>();
	readonly date = input.required<string>();
	readonly name = input.required<string>();
	readonly amountGrosze = input.required<number>();
	readonly currency = input<string | null>(null);
	readonly plnGrosze = input<number | null>(null);
	readonly exchangeRate = input<string | null>(null);
	readonly exchangeRateDate = input<string | null>(null);
	readonly plnConversionMissing = input<boolean>(false);
	readonly confirmAt = input<string | null>(null);
	readonly bonusComment = input<string | null>(null);
	readonly status = input.required<SettlementBonusStatus>();
	readonly rejectedAt = input<string | null>(null);
	readonly rejectReason = input<string | null>(null);
	readonly actions = input.required<SettlementBonusRowActions>();
	readonly disabled = input<boolean>(false);
	readonly settlementDayTo = input<string | null>(null);
	readonly termsInMonth = input<number>(0);
	readonly bonusDate = input<string | null>(null);

	readonly rejected = computed(() => this.status() === 'REJECTED');

	readonly counted = computed(() => this.status() === 'CONFIRMED' || this.status() === 'SETTLED');

	readonly isForeign = computed(() => {
		const currency = this.currency();
		return !!currency && currency !== 'PLN';
	});

	readonly displayGrosze = computed(() => this.plnGrosze() ?? this.amountGrosze());

	readonly showRate = computed(() => this.isForeign() && !!this.exchangeRate() && !!this.exchangeRateDate());

	readonly rateTooltip = computed(() =>
		this.showRate()
			? `Kurs NBP ${this.exchangeRate()} z ${this.exchangeRateDate()} — ostatnie notowanie przed dniem rozliczenia.`
			: 'Terminy w tym miesiącu przeliczono różnymi kursami — szczegóły w dodatku.',
	);

	readonly dayRange = computed(() => {
		const to = this.settlementDayTo();
		return to && to.slice(0, 10) !== this.date().slice(0, 10) ? to : null;
	});

	readonly termsLabel = computed(() => {
		const count = this.termsInMonth();
		return count > 1 ? `${count} terminów w tym miesiącu` : null;
	});

	readonly foreignBonusDate = computed(() => {
		const bonusDate = this.bonusDate();
		if (!bonusDate || this.termsInMonth() === 0) return null;
		return bonusDate.slice(0, 7) !== this.date().slice(0, 7) ? bonusDate : null;
	});

	private readonly state = inject(DriversSalaryV2StateService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly dialog = inject(MatDialog);

	confirm(): void {
		this.state.confirmBonus(this.bonusId());
	}

	unlock(): void {
		this.state.unlockBonus(this.bonusId());
	}

	restore(): void {
		this.state.restoreBonus(this.bonusId());
	}

	reject(): void {
		this.dialog
			.open(RejectBonusDialogComponent, { width: '440px', autoFocus: 'dialog' })
			.afterClosed()
			.pipe(
				filter((reason): reason is string | null => reason !== undefined),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((reason) => this.state.rejectBonus(this.bonusId(), reason));
	}
}
